import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { ROLES } from '@bcc/shared';
import { SITE_FORM_CONTROL, SITE_GLASS_CARD } from '../../constants/siteUi';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

type AiApiSummary = {
  openai: { stored_key: boolean; environment_fallback: boolean };
  anthropic: { stored_key: boolean; environment_fallback: boolean };
  custom_openai: { configured: boolean };
  openai_model: string | null;
  anthropic_model: string | null;
  custom_openai_model: string | null;
  active_provider: 'openai' | 'anthropic' | 'custom_openai' | null;
  analyzer_ready: boolean;
  analyzer_environment_fallback: boolean;
  effective_runtime_provider: 'openai' | 'anthropic' | 'custom_openai' | null;
  dashboard_runtime_provider: 'openai' | 'anthropic' | 'custom_openai' | null;
  enforce_organization_ai_keys: boolean;
};

function providerLabel(p: 'openai' | 'anthropic' | 'custom_openai' | null): string {
  if (p === 'custom_openai') return 'Custom';
  if (p === 'anthropic') return 'Anthropic';
  if (p === 'openai') return 'OpenAI';
  return 'auto';
}

/** Admin-only — keys + provider choice persisted in `site_content`; deal analyzer uses the selected vendor. */
export function AdminAiApiSettings() {
  const { session, role } = useAuth();
  const token = session?.access_token;
  const [summary, setSummary] = useState<AiApiSummary | null>(null);
  const [openaiKey, setOpenaiKey] = useState('');
  const [anthropicKey, setAnthropicKey] = useState('');
  const [customBaseUrl, setCustomBaseUrl] = useState('');
  const [customApiKey, setCustomApiKey] = useState('');
  const [openaiModel, setOpenaiModel] = useState('');
  const [anthropicModel, setAnthropicModel] = useState('');
  const [customModel, setCustomModel] = useState('');
  const [provider, setProvider] = useState<'openai' | 'anthropic' | 'custom_openai'>('openai');
  const [enforceOrgKeys, setEnforceOrgKeys] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!token || role !== ROLES.ADMIN) return;
    setErr(null);
    try {
      const res = await apiFetch<AiApiSummary>('/api/admin/ai-api-settings', { token });
      setSummary(res);
      setOpenaiModel(res.openai_model ?? '');
      setAnthropicModel(res.anthropic_model ?? '');
      setCustomModel(res.custom_openai_model ?? '');
      setEnforceOrgKeys(Boolean(res.enforce_organization_ai_keys));
      if (res.active_provider === 'openai' || res.active_provider === 'anthropic' || res.active_provider === 'custom_openai') {
        setProvider(res.active_provider);
      } else if (res.custom_openai.configured && !res.openai.stored_key && !res.anthropic.stored_key) {
        setProvider('custom_openai');
      } else if (res.anthropic.stored_key && !res.openai.stored_key) {
        setProvider('anthropic');
      } else {
        setProvider('openai');
      }
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [token, role]);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveKeys(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setErr(null);
    setOk(null);
    setLoading(true);
    try {
      const patch: Record<string, unknown> = {
        active_provider: provider,
        openai_model: openaiModel.trim() || null,
        anthropic_model: anthropicModel.trim() || null,
        custom_openai_model: customModel.trim() || null,
        enforce_organization_ai_keys: enforceOrgKeys,
      };
      if (openaiKey.trim()) patch.openai_api_key = openaiKey.trim();
      if (anthropicKey.trim()) patch.anthropic_api_key = anthropicKey.trim();
      if (customBaseUrl.trim()) patch.custom_openai_base_url = customBaseUrl.trim();
      if (customApiKey.trim()) patch.custom_openai_api_key = customApiKey.trim();
      await apiFetch<{ summary: AiApiSummary }>('/api/admin/ai-api-settings', {
        method: 'PATCH',
        token,
        body: JSON.stringify(patch),
      });
      setOpenaiKey('');
      setAnthropicKey('');
      setCustomApiKey('');
      setOk('Saved.');
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function clearOpenAiKey() {
    if (!token) return;
    setErr(null);
    setOk(null);
    setLoading(true);
    try {
      await apiFetch<{ summary: AiApiSummary }>('/api/admin/ai-api-settings', {
        method: 'PATCH',
        token,
        body: JSON.stringify({ openai_api_key: '' }),
      });
      setOk('Stored OpenAI key removed.');
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function clearAnthropicKey() {
    if (!token) return;
    setErr(null);
    setOk(null);
    setLoading(true);
    try {
      await apiFetch<{ summary: AiApiSummary }>('/api/admin/ai-api-settings', {
        method: 'PATCH',
        token,
        body: JSON.stringify({ anthropic_api_key: '' }),
      });
      setOk('Stored Anthropic key removed.');
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function clearCustomProvider() {
    if (!token) return;
    setErr(null);
    setOk(null);
    setLoading(true);
    try {
      await apiFetch<{ summary: AiApiSummary }>('/api/admin/ai-api-settings', {
        method: 'PATCH',
        token,
        body: JSON.stringify({
          custom_openai_api_key: '',
          custom_openai_base_url: '',
          custom_openai_model: null,
        }),
      });
      setCustomBaseUrl('');
      setOk('Stored custom provider credentials removed.');
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  if (role !== ROLES.ADMIN) {
    return (
      <div className={`p-6 ${SITE_GLASS_CARD}`}>
        <p className="text-sm text-[var(--home-muted-on-dark)]">Only administrators can manage AI API keys.</p>
      </div>
    );
  }

  const dashCount = summary
    ? Number(summary.openai.stored_key) + Number(summary.anthropic.stored_key) + Number(summary.custom_openai.configured)
    : 0;

  const needsExplicitChoice = Boolean(
    summary && dashCount >= 2 && !summary.active_provider && !summary.analyzer_ready,
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-body text-2xl font-semibold text-[var(--home-text-on-dark)]">AI API Settings</h1>
        <p className="mt-2 max-w-2xl text-sm text-[var(--home-muted-on-dark)]">
          Organization-wide defaults for the Deal Analyzer. The green <strong className="text-[var(--home-text-on-dark)]">Active</strong>{' '}
          badge means credentials are saved <strong className="text-[var(--home-text-on-dark)]">in this dashboard</strong> — not only in
          server environment variables. Use{' '}
          <strong className="text-[var(--home-text-on-dark)]">Global — organization keys only</strong> to lock investor personal keys so
          everyone uses these organization credentials (dashboard + env fallbacks where applicable).
        </p>
      </div>

      <div className={`p-6 ${SITE_GLASS_CARD}`}>
        {summary ? (
          <>
            <div className="mb-6 flex flex-wrap items-center gap-3 border-b border-white/[0.08] pb-6">
              <span className="text-sm font-medium text-[var(--home-text-on-dark)]">Deal analyzer</span>
              <span
                className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                  summary.analyzer_ready ? 'bg-emerald-500/20 text-emerald-200' : 'bg-amber-500/15 text-amber-200'
                }`}
              >
                {summary.analyzer_ready ? 'Active' : 'Locked'}
              </span>
              {summary.analyzer_ready ? (
                <span className="text-xs text-[var(--home-muted-on-dark)]">
                  Dashboard:{' '}
                  <strong className="text-[var(--home-text-on-dark)]">{providerLabel(summary.dashboard_runtime_provider)}</strong>
                  {summary.enforce_organization_ai_keys ? (
                    <span className="text-amber-200/95"> — Global mode (personal keys locked)</span>
                  ) : null}
                </span>
              ) : summary.analyzer_environment_fallback ? (
                <span className="text-xs text-amber-100/95">
                  Runtime uses <strong>{providerLabel(summary.effective_runtime_provider)}</strong> from{' '}
                  <strong>server environment only</strong> — save keys here for dashboard Active status.
                </span>
              ) : null}
            </div>

            {summary.analyzer_environment_fallback && !summary.analyzer_ready ? (
              <div className="mb-6 rounded-xl border border-amber-400/35 bg-amber-950/35 px-4 py-3 text-sm text-amber-100">
                Investors may still run the analyzer using{' '}
                <strong className="text-amber-50">environment variables on the API server</strong>. This panel stays{' '}
                <strong>Locked</strong> until you store keys or a custom endpoint below.
              </div>
            ) : null}

            {needsExplicitChoice ? (
              <div className="mb-6 rounded-xl border border-amber-400/35 bg-amber-950/35 px-4 py-3 text-sm text-amber-100">
                Multiple providers are configured in the dashboard — pick which one runs the deal analyzer below, then save.
              </div>
            ) : null}
            <ul className="mb-6 space-y-3 border-b border-white/[0.08] pb-6 text-sm">
              <li className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-[var(--home-text-on-dark)]">OpenAI</span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                    summary.openai.stored_key ? 'bg-emerald-500/20 text-emerald-200' : 'bg-white/10 text-[var(--home-muted-on-dark)]'
                  }`}
                >
                  {summary.openai.stored_key ? 'Key saved' : 'No dashboard key'}
                </span>
                {summary.openai.environment_fallback ? (
                  <span className="text-[var(--home-muted-on-dark)]">(Env fallback on server)</span>
                ) : (
                  <span className="text-[var(--home-muted-on-dark)]">(No env key)</span>
                )}
              </li>
              <li className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-[var(--home-text-on-dark)]">Anthropic</span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                    summary.anthropic.stored_key ? 'bg-emerald-500/20 text-emerald-200' : 'bg-white/10 text-[var(--home-muted-on-dark)]'
                  }`}
                >
                  {summary.anthropic.stored_key ? 'Key saved' : 'No dashboard key'}
                </span>
                {summary.anthropic.environment_fallback ? (
                  <span className="text-[var(--home-muted-on-dark)]">(Env fallback on server)</span>
                ) : (
                  <span className="text-[var(--home-muted-on-dark)]">(No env key)</span>
                )}
              </li>
              <li className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-[var(--home-text-on-dark)]">Custom</span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                    summary.custom_openai.configured ? 'bg-emerald-500/20 text-emerald-200' : 'bg-white/10 text-[var(--home-muted-on-dark)]'
                  }`}
                >
                  {summary.custom_openai.configured ? 'Configured' : 'Not configured'}
                </span>
                <span className="text-[var(--home-muted-on-dark)]">(OpenAI-compatible HTTPS base URL)</span>
              </li>
            </ul>
          </>
        ) : null}

        {err ? <p className="mb-4 text-sm text-red-400">{err}</p> : null}
        {ok ? <p className="mb-4 text-sm text-emerald-400">{ok}</p> : null}

        <form className="space-y-5" onSubmit={saveKeys}>
          <fieldset className="space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <legend className="px-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">
              Global access control
            </legend>
            <label className="flex cursor-pointer items-start gap-3 text-sm text-[var(--home-text-on-dark)]">
              <input
                type="checkbox"
                className="mt-1 accent-[var(--home-miami-blue)]"
                checked={enforceOrgKeys}
                onChange={(e) => setEnforceOrgKeys(e.target.checked)}
              />
              <span>
                <span className="font-medium">Global — organization keys only</span>
                <span className="mt-1 block text-xs font-normal text-[var(--home-muted-on-dark)]">
                  Everyone uses organization credentials below (including env fallbacks on the server). Investors cannot edit personal API
                  keys. Requires a working organization configuration first.
                </span>
              </span>
            </label>
          </fieldset>

          <fieldset className="space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <legend className="px-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">
              Deal analyzer provider
            </legend>
            <label className="flex cursor-pointer items-center gap-3 text-sm text-[var(--home-text-on-dark)]">
              <input
                type="radio"
                name="ai-provider"
                className="accent-[var(--home-miami-blue)]"
                checked={provider === 'openai'}
                disabled={!summary?.openai.stored_key && !summary?.openai.environment_fallback}
                onChange={() => setProvider('openai')}
              />
              <span>
                OpenAI{' '}
                {!summary?.openai.stored_key && !summary?.openai.environment_fallback ? '(add a key or env OPENAI_API_KEY)' : ''}
              </span>
            </label>
            <label className="flex cursor-pointer items-center gap-3 text-sm text-[var(--home-text-on-dark)]">
              <input
                type="radio"
                name="ai-provider"
                className="accent-[var(--home-miami-blue)]"
                checked={provider === 'anthropic'}
                disabled={!summary?.anthropic.stored_key && !summary?.anthropic.environment_fallback}
                onChange={() => setProvider('anthropic')}
              />
              <span>
                Anthropic{' '}
                {!summary?.anthropic.stored_key && !summary?.anthropic.environment_fallback ? '(add a key or env ANTHROPIC_API_KEY)' : ''}
              </span>
            </label>
            <label className="flex cursor-pointer items-center gap-3 text-sm text-[var(--home-text-on-dark)]">
              <input
                type="radio"
                name="ai-provider"
                className="accent-[var(--home-miami-blue)]"
                checked={provider === 'custom_openai'}
                onChange={() => setProvider('custom_openai')}
              />
              <span>Custom (OpenAI-compatible Chat Completions)</span>
            </label>
          </fieldset>

          <div className="space-y-4 rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">Custom provider</p>
            <label className="block space-y-1.5 text-xs">
              <span className="text-[var(--home-muted-on-dark)]">Base URL (HTTPS)</span>
              <input
                type="url"
                autoComplete="off"
                value={customBaseUrl}
                onChange={(e) => setCustomBaseUrl(e.target.value)}
                placeholder="https://your-host/v1"
                className={`w-full ${SITE_FORM_CONTROL}`}
              />
            </label>
            <label className="block space-y-1.5 text-xs">
              <span className="text-[var(--home-muted-on-dark)]">API key</span>
              <input
                type="password"
                autoComplete="off"
                value={customApiKey}
                onChange={(e) => setCustomApiKey(e.target.value)}
                placeholder="Leave blank to keep current stored key"
                className={`w-full ${SITE_FORM_CONTROL}`}
              />
            </label>
            <label className="block space-y-1.5 text-xs">
              <span className="text-[var(--home-muted-on-dark)]">Model id</span>
              <input
                type="text"
                value={customModel}
                onChange={(e) => setCustomModel(e.target.value)}
                placeholder="e.g. meta-llama/Llama-3-70b"
                className={`w-full ${SITE_FORM_CONTROL}`}
              />
            </label>
            <button
              type="button"
              disabled={loading || !summary?.custom_openai.configured}
              onClick={() => void clearCustomProvider()}
              className="rounded-lg border border-white/15 px-3 py-2 text-xs font-medium text-[var(--home-muted-on-dark)] transition hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-40"
            >
              Remove stored custom provider
            </button>
          </div>

          <label className="block space-y-1.5 text-xs">
            <span className="text-[var(--home-muted-on-dark)]">OpenAI API key</span>
            <input
              type="password"
              autoComplete="off"
              value={openaiKey}
              onChange={(e) => setOpenaiKey(e.target.value)}
              placeholder="sk-… (leave blank to keep current stored key)"
              className={`w-full ${SITE_FORM_CONTROL}`}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={loading || !summary?.openai.stored_key}
              onClick={() => void clearOpenAiKey()}
              className="rounded-lg border border-white/15 px-3 py-2 text-xs font-medium text-[var(--home-muted-on-dark)] transition hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-40"
            >
              Remove stored OpenAI key
            </button>
          </div>

          <label className="block space-y-1.5 text-xs">
            <span className="text-[var(--home-muted-on-dark)]">Anthropic API key</span>
            <input
              type="password"
              autoComplete="off"
              value={anthropicKey}
              onChange={(e) => setAnthropicKey(e.target.value)}
              placeholder="sk-ant-… (leave blank to keep current stored key)"
              className={`w-full ${SITE_FORM_CONTROL}`}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={loading || !summary?.anthropic.stored_key}
              onClick={() => void clearAnthropicKey()}
              className="rounded-lg border border-white/15 px-3 py-2 text-xs font-medium text-[var(--home-muted-on-dark)] transition hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-40"
            >
              Remove stored Anthropic key
            </button>
          </div>

          <label className="block space-y-1.5 text-xs">
            <span className="text-[var(--home-muted-on-dark)]">OpenAI model override</span>
            <input
              type="text"
              value={openaiModel}
              onChange={(e) => setOpenaiModel(e.target.value)}
              placeholder="e.g. gpt-4o (empty = env default)"
              className={`w-full ${SITE_FORM_CONTROL}`}
            />
          </label>

          <label className="block space-y-1.5 text-xs">
            <span className="text-[var(--home-muted-on-dark)]">Anthropic model override</span>
            <input
              type="text"
              value={anthropicModel}
              onChange={(e) => setAnthropicModel(e.target.value)}
              placeholder="e.g. claude-3-5-sonnet-20241022 (empty = default)"
              className={`w-full ${SITE_FORM_CONTROL}`}
            />
          </label>

          <button
            type="submit"
            disabled={loading || !token}
            className="rounded-xl bg-[var(--home-miami-blue)] px-5 py-2.5 text-sm font-semibold text-[var(--home-zone-void)] transition hover:brightness-105 disabled:opacity-50"
          >
            {loading ? 'Saving…' : 'Save settings'}
          </button>
        </form>
      </div>
    </div>
  );
}
